import { leads } from './leads'
import { realtors } from './realtors'
import { realtorProspects } from './prospects'
import { pastClients } from './pastClients'
import type {
  ActivityCategory,
  ActivityContact,
  ContactType,
} from '../types/activity'

export const contactTypeMeta: Record<
  ContactType,
  { label: string; classes: string }
> = {
  realtor: {
    label: 'Partner',
    classes: 'bg-teal-50 text-teal-700 ring-teal-200',
  },
  prospect: {
    label: 'Prospect',
    classes: 'bg-brand-50 text-brand-700 ring-brand-200',
  },
  lead: {
    label: 'Lead',
    classes: 'bg-warn-50 text-warn-700 ring-warn-200',
  },
  'past-client': {
    label: 'Past client',
    classes: 'bg-success-50 text-success-700 ring-success-200',
  },
}

/**
 * One directory of everyone an activity can be attributed to. Activities are
 * always tied to a realtor partner, a realtor prospect, a lead, or a past client.
 */
export const contactDirectory: ActivityContact[] = [
  ...realtors.map((realtor) => ({
    id: realtor.id,
    name: realtor.name,
    detail: `${realtor.company} · ${realtor.partnerType} partner`,
    type: 'realtor' as ContactType,
  })),
  ...realtorProspects.map((prospect) => ({
    id: prospect.id,
    name: prospect.name,
    detail: `${prospect.company} · ${prospect.market}`,
    type: 'prospect' as ContactType,
  })),
  ...leads.map((lead) => ({
    id: lead.id,
    name: lead.name,
    detail: `${lead.purpose} · ${lead.amount} · ${lead.market}`,
    type: 'lead' as ContactType,
  })),
  ...pastClients.map((client) => ({
    id: client.id,
    name: client.name,
    detail: `${client.closedLabel} · ${client.program}`,
    type: 'past-client' as ContactType,
  })),
]

export const contactMap: Record<string, ActivityContact> =
  contactDirectory.reduce<Record<string, ActivityContact>>((map, contact) => {
    map[contact.id] = contact
    return map
  }, {})

/** Contacts that are not database records yet (new listing agents, referrals). */
export const externalContacts: ActivityContact[] = [
  {
    id: 'RP-108',
    name: 'Chris Vogel',
    detail: 'Anchor Realty · listing agent on loan 4482',
    type: 'prospect',
  },
]

export function findContact(id: string): ActivityContact {
  return (
    contactMap[id] ??
    externalContacts.find((contact) => contact.id === id) ?? {
      id,
      name: 'Unknown contact',
      detail: 'Not in the database',
      type: 'lead',
    }
  )
}

/** Which contact types make sense first for each activity type. */
export const preferredContactTypes: Record<ActivityCategory, ContactType[]> = {
  'realtor-outreach': ['prospect', 'realtor'],
  'consumer-conversation': ['lead', 'past-client'],
  'social-engagement': ['prospect', 'realtor'],
  'past-client-call': ['past-client'],
  'partner-meeting': ['realtor', 'prospect'],
}

export function contactsForActivity(
  category: ActivityCategory,
): ActivityContact[] {
  const preferred = preferredContactTypes[category]
  return [...contactDirectory].sort((a, b) => {
    const aRank = preferred.indexOf(a.type)
    const bRank = preferred.indexOf(b.type)
    return (
      (aRank === -1 ? preferred.length : aRank) -
      (bRank === -1 ? preferred.length : bRank)
    )
  })
}
